import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Users, Save, GraduationCap, Eye } from "lucide-react";

type AudienceRole = "entrepreneur" | "mentor" | "admin" | "instructor";

const AUDIENCE_ROLE_OPTIONS: { value: AudienceRole; label: string }[] = [
  { value: "entrepreneur", label: "Entrepreneurs" },
  { value: "mentor", label: "Mentors" },
  { value: "admin", label: "Admins" },
  { value: "instructor", label: "Instructors" },
];

interface CurriculumWithAudiences {
  id: string;
  title: string;
  is_published: boolean;
  audiences: string[];
}

export function CurriculumVisibilityManager() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [changes, setChanges] = useState<Record<string, string[]>>({});
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch all curricula with their audiences
  const { data: curricula, isLoading } = useQuery({
    queryKey: ["admin-curricula-visibility"],
    queryFn: async () => {
      const { data: curriculaData, error } = await supabase
        .from("curricula")
        .select("id, title, is_published")
        .order("sequence_order", { ascending: true });

      if (error) throw error;

      // Fetch audiences for all curricula
      const { data: audienceData, error: audienceError } = await supabase
        .from("curriculum_audiences")
        .select("curriculum_id, audience_role");

      if (audienceError) throw audienceError;

      // Map audiences to curricula
      const audienceMap = new Map<string, string[]>();
      (audienceData || []).forEach((a) => {
        const existing = audienceMap.get(a.curriculum_id) || [];
        audienceMap.set(a.curriculum_id, [...existing, a.audience_role]);
      });

      return (curriculaData || []).map((c) => ({
        ...c,
        // Zero curriculum_audiences rows means HIDDEN FROM ALL learners --
        // useCurriculumEligibility.checkUserEligibility treats no rows as
        // deny-all-except-admin/instructor. Defaulting to all four roles here
        // displayed the exact opposite of reality.
        audiences: audienceMap.get(c.id) || [],
      })) as CurriculumWithAudiences[];
    },
  });

  // Initialize changes from current data
  useEffect(() => {
    if (curricula && Object.keys(changes).length === 0) {
      const initialChanges: Record<string, string[]> = {};
      curricula.forEach((c) => {
        initialChanges[c.id] = c.audiences;
      });
      setChanges(initialChanges);
    }
  }, [curricula]);

  const handleAudienceToggle = (curriculumId: string, role: AudienceRole) => {
    setChanges((prev) => {
      const currentAudiences = prev[curriculumId] || [];
      const newAudiences = currentAudiences.includes(role)
        ? currentAudiences.filter((r) => r !== role)
        : [...currentAudiences, role];
      return { ...prev, [curriculumId]: newAudiences };
    });
    setHasChanges(true);
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      // `changes` is seeded with EVERY curriculum on load, so iterating it blindly
      // deleted and reinserted audience rows for all of them on every save. That
      // is non-transactional, so a mid-loop failure could leave curricula with no
      // audience rows at all -- i.e. hidden from everyone. Only touch the ones
      // whose audience set genuinely differs.
      const original = new Map(
        (curricula || []).map((c) => [c.id, [...c.audiences].sort().join(",")])
      );

      const dirty = Object.entries(changes).filter(
        ([id, audiences]) => original.get(id) !== [...audiences].sort().join(",")
      );

      for (const [curriculumId, audiences] of dirty) {
        const { error: delError } = await supabase
          .from("curriculum_audiences")
          .delete()
          .eq("curriculum_id", curriculumId);
        if (delError) throw delError;

        if (audiences.length > 0) {
          const inserts = audiences.map((role) => ({
            curriculum_id: curriculumId,
            audience_role: role,
          }));
          const { error } = await supabase
            .from("curriculum_audiences")
            .insert(inserts);
          if (error) throw error;
        }
      }

      return dirty.length;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-curricula-visibility"] });
      queryClient.invalidateQueries({ queryKey: ["curricula"] });
      // TWO different featured-curricula queries exist and both must be
      // invalidated:
      //   - ["featured-curricula", user?.id]     -> useCurricula.ts (signed-in)
      //   - ["featured-curricula-landing"]       -> src/pages/Index.tsx (public)
      // The audit said the "-landing" key "doesn't exist"; it does — Index.tsx
      // registers it. Invalidating only one leaves the other stale.
      queryClient.invalidateQueries({ queryKey: ["featured-curricula"] });
      queryClient.invalidateQueries({ queryKey: ["featured-curricula-landing"] });
      toast({
        title: "Visibility updated",
        description: "Curriculum visibility settings have been saved.",
      });
      setHasChanges(false);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to save visibility settings.",
        variant: "destructive",
      });
    },
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Curriculum Visibility
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (!curricula || curricula.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Curriculum Visibility
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-center py-8">
            No curricula available to configure.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Curriculum Visibility
          </CardTitle>
          <CardDescription className="mt-1.5">
            Control which user roles can see each curriculum
          </CardDescription>
        </div>
        {hasChanges && (
          <Button
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
            size="sm"
          >
            <Save className="h-4 w-4 mr-2" />
            {saveMutation.isPending ? "Saving..." : "Save Changes"}
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {curricula.map((curriculum) => (
            <div
              key={curriculum.id}
              className="p-4 border rounded-lg space-y-3"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <GraduationCap className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">{curriculum.title}</span>
                  <Badge variant={curriculum.is_published ? "default" : "secondary"}>
                    {curriculum.is_published ? "Published" : "Draft"}
                  </Badge>
                </div>
              </div>
              <div className="flex flex-wrap gap-4">
                {AUDIENCE_ROLE_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <Checkbox
                      checked={(changes[curriculum.id] || curriculum.audiences).includes(option.value)}
                      onCheckedChange={() => handleAudienceToggle(curriculum.id, option.value)}
                    />
                    <span className="text-sm">{option.label}</span>
                  </label>
                ))}
              </div>
              {(changes[curriculum.id] || curriculum.audiences).length === 0 && (
                <p className="mt-2 text-sm text-destructive">
                  Hidden from all learners — no audience selected.
                </p>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
